import React, { createRef } from 'react';

// constants
import { ADD_CUSTOMER_TOOLTIP, BACK } from './constants';

import TopToolbar from '../global/subcomponents/topToolbar/TopToolbar';

import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import { Button, TextField } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import PhoneIcon from '@material-ui/icons/Phone';
import { AddAPhoto, Camera } from '@material-ui/icons';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import debounce from 'lodash/debounce';
import './Customer.css';
import { BASE_URL } from '../global/Server/endpoints';
import BlacklistWarningPanel from './BlacklistWarningPanel';
import {
  canUseBlacklist,
  canUseMatchRules,
} from '../../constants/planFeatures';
import DuplicateCustomerPanel from './DuplicateCustomerPanel';
import WriteGuard from '../global/ReadOnly/WriteGuard';
import {
  attachStream,
  captureFrame,
  dataURLToPhotoFile,
  describeCameraError,
  listVideoInputs,
  openCameraStream,
  stopStream,
} from './cameraUtils';
import { CAMERA_ROLES, resolveCameraDeviceId } from '../Utils/cameraSettings';

const DEFAULT_POINTS = 20;

class CustomerForm extends React.Component {
  constructor(props) {
    super(props);
    const isIOSSafari = /iP(ad|hone|od).*Safari/i.test(navigator.userAgent);
    this.videoRef = createRef();
    this.canvasRef = createRef();
    this.state = {
      firstName: '',
      lastName: '',
      dob: null,
      comments: '',
      phone: '',
      files: [],
      newFiles: [],
      capturedImageURL: null,
      showVideo: false,
      isCameraOpen: false,
      cameraFacingMode: 'environment',
      isMobile: window.matchMedia('(max-width: 800px)').matches || isIOSSafari,

      // ── Camera health ────────────────────────────────────────────────
      // Photos were saving as blank "camera off" frames because none of
      // this was tracked: failures were swallowed, and Capture was live
      // before the camera had sent a single frame.
      cameraError: '', // getUserMedia / readiness failure, shown to the employee
      cameraStarting: false, // opening the stream
      cameraReady: false, // frames are actually arriving — gates Capture
      videoInputs: [], // available cameras (labels need permission first)
      selectedCameraId: '', // '' = let the browser pick
      // A captured frame that failed the blank-frame check. Held back from
      // `files` so Save stays blocked, with an explicit override offered.
      rejectedFrameURL: null,
      rejectedFrameReason: '',

      blacklistMatches: [],
      blacklistDismissed: false,

      duplicateNameMatches: [],
      duplicateNameDismissed: false,

      duplicatePhoneMatches: [],
      duplicatePhoneDismissed: false,

      saving: false,
    };
  }

  componentWillUnmount() {
    stopStream(this.videoRef.current);
  }

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleDateChange = (date, key) => {
    this.setState({ [key]: date });
  };

  handleNameChange = (e) => {
    const { name, value } = e.target;

    this.setState({ [name]: value, duplicateNameDismissed: false }, () => {
      const { firstName, lastName } = this.state;

      if (firstName && lastName) {
        this.debouncedCheckDuplicateNameMatches(firstName, lastName);
      }

      if (name === 'firstName' || name === 'lastName') {
        this.setState({ blacklistDismissed: false });
        this.debouncedCheckBlacklistMatch();
      }
    });
  };

  handlePhoneChange = (event) => {
    const { name, value } = event.target;
    let formattedValue = value.replace(/[^0-9]/g, '');
    if (formattedValue.length > 9) {
      formattedValue = formattedValue.substring(0, 10);
    }
    this.setState(
      {
        [name]: formattedValue,
        duplicatePhoneDismissed: false,
        blacklistDismissed: false,
      },
      () => {
        this.debouncedCheckDuplicatePhone(formattedValue);
        this.debouncedCheckBlacklistMatch();
      }
    );
  };

  checkBlacklistMatch = async () => {
    // No Black List on this plan means no lookup and no panel. Returning
    // early rather than only hiding the panel matters: the request itself
    // is the feature, and /customer/blacklistSearch is reachable by any
    // ROLE_CUSTOMER holder, so a Basic company would otherwise be querying
    // a list it does not have.
    if (!canUseBlacklist(this.props.user)) {
      if (this.state.blacklistMatches.length) {
        this.setState({ blacklistMatches: [] });
      }
      return;
    }
    const firstName = (this.state.firstName || '').trim();
    const lastName = (this.state.lastName || '').trim();
    const phone = (this.state.phone || '').trim();

    const hasSearch =
      firstName.length >= 2 || lastName.length >= 2 || phone.length >= 2;
    if (!hasSearch) {
      this.setState({ blacklistMatches: [] });
      return;
    }

    try {
      const locationId = this.props.user?.selectedLocationId;
      const token = localStorage.getItem('token');

      const body = { locationId };
      if (firstName.length >= 2) body.firstName = firstName;
      if (lastName.length >= 2) body.lastName = lastName;
      if (phone.length >= 2) body.phone = phone;

      const response = await fetch(`${BASE_URL}/customer/blacklistSearch`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        this.setState({ blacklistMatches: data.customers || [] });
      }
    } catch (err) {
      // silent
    }
  };

  debouncedCheckBlacklistMatch = debounce(this.checkBlacklistMatch, 400);

  /**
   * What this banner promises has to match what the customer profile will
   * actually offer once the record is saved. It used to name Points, Match
   * Rules and Wallet Pass unconditionally — but Match Rules is a paid-tier
   * feature and Wallet Pass needs ROLE_CUSTOMER_WALLET_PASS, so a Basic
   * company was told about two tabs it would never see.
   */
  postSaveFeatureSentence = () => {
    const user = this.props.user;
    const features = ['Points'];
    if (canUseMatchRules(user)) features.push('Match Rules');
    if ((user?.authority || []).includes('ROLE_CUSTOMER_WALLET_PASS')) {
      features.push('Wallet Pass');
    }
    const list =
      features.length === 1
        ? features[0]
        : `${features.slice(0, -1).join(', ')} and ${
            features[features.length - 1]
          }`;
    return `${list} can be configured from the customer profile after saving.`;
  };

  checkDuplicatePhone = (phone) => {
    const trimmed = (phone || '').replace(/\D/g, '');
    if (trimmed.length < 7) {
      this.setState({ duplicatePhoneMatches: [] });
      return;
    }
    try {
      const locationId = this.props.user.selectedLocationId;
      this.props.validateCustomerName(
        {
          type: 'phone',
          phone: trimmed,
          locationId,
          customerId: '',
        },
        (success, message, customers) => {
          this.setState({
            duplicatePhoneMatches: customers || [],
            duplicatePhoneDismissed: false,
          });
        }
      );
    } catch (err) {
      // silent
    }
  };

  checkDuplicateName = (firstName, lastName) => {
    if (!firstName?.trim() || !lastName?.trim()) {
      this.setState({ duplicateNameMatches: [] });
      return;
    }
    try {
      const locationId = this.props.user.selectedLocationId;
      this.props.validateCustomerName(
        {
          type: 'name',
          firstName,
          lastName,
          locationId,
          customerId: '',
        },
        (success, message, customers) => {
          this.setState({
            duplicateNameMatches: customers || [],
            duplicateNameDismissed: false,
          });
        }
      );
    } catch (err) {
      // silent
    }
  };

  debouncedCheckDuplicatePhone = debounce(this.checkDuplicatePhone, 400);

  debouncedCheckDuplicateNameMatches = debounce(this.checkDuplicateName, 400);

  // ── Camera ────────────────────────────────────────────────────────────────
  // `deviceId` is honoured so the employee can move off a virtual/stub camera;
  // readiness is awaited so Capture can never fire against a 0x0 video.
  startWebcam = async (deviceId) => {
    if (!this.videoRef.current) return;
    this.setState({
      cameraStarting: true,
      cameraError: '',
      cameraReady: false,
    });
    try {
      const stream = await openCameraStream({
        // Manage Rule's general camera, unless the employee picked one.
        deviceId:
          deviceId ||
          this.state.selectedCameraId ||
          (await resolveCameraDeviceId(CAMERA_ROLES.GENERAL)),
      });
      if (!this.videoRef.current) {
        // Unmounted or closed while the permission prompt was up.
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      await attachStream(this.videoRef.current, stream);
      // Labels are only readable once permission has been granted, so the
      // device list is (re)built here rather than on mount.
      const videoInputs = await listVideoInputs();
      const active = stream.getVideoTracks()[0];
      const activeId = active?.getSettings ? active.getSettings().deviceId : '';
      this.setState({
        cameraStarting: false,
        cameraReady: true,
        cameraError: '',
        videoInputs,
        selectedCameraId: deviceId || activeId || this.state.selectedCameraId,
      });
    } catch (err) {
      stopStream(this.videoRef.current);
      this.setState({
        cameraStarting: false,
        cameraReady: false,
        showVideo: false,
        isCameraOpen: false,
        cameraError: describeCameraError(err),
      });
    }
  };

  handleSelectCamera = (e) => {
    const deviceId = e.target.value;
    this.setState({ selectedCameraId: deviceId }, () => {
      stopStream(this.videoRef.current);
      if (this.state.showVideo) this.startWebcam(deviceId);
      else if (this.state.isCameraOpen) this.startCameraMobile(deviceId);
    });
  };

  handleTakePhotoClick = () => {
    this.setState(
      {
        showVideo: true,
        capturedImageURL: null,
        files: [],
        newFiles: [],
        cameraError: '',
        rejectedFrameURL: null,
        rejectedFrameReason: '',
      },
      () => {
        this.startWebcam();
      }
    );
  };

  captureImage = () => {
    const result = captureFrame(this.videoRef.current, this.canvasRef.current, {
      quality: 0.5,
    });
    this.handleCaptureResult(result, this.stopCamera);
  };

  /**
   * Single place where a capture becomes (or fails to become) an upload.
   * A frame that fails inspection is parked in `rejectedFrameURL` instead of
   * `files`, which keeps Save disabled while still letting the employee
   * deliberately override.
   */
  handleCaptureResult = (result, closeCamera) => {
    if (!result.dataURL) {
      this.setState({ cameraError: result.reason });
      return;
    }
    if (!result.ok) {
      this.setState({
        rejectedFrameURL: result.dataURL,
        rejectedFrameReason: result.reason,
        capturedImageURL: null,
        cameraError: '',
      });
      if (closeCamera) closeCamera();
      return;
    }
    this.setState(
      {
        capturedImageURL: result.dataURL,
        rejectedFrameURL: null,
        rejectedFrameReason: '',
        cameraError: '',
      },
      () => {
        if (closeCamera) closeCamera();
      }
    );
    this.handleFileAdd(result.dataURL);
  };

  // Deliberate override for the rare case the blank-frame check is wrong and
  // the employee can see a usable picture.
  handleUseRejectedFrame = () => {
    const dataURL = this.state.rejectedFrameURL;
    if (!dataURL) return;
    this.setState({
      capturedImageURL: dataURL,
      rejectedFrameURL: null,
      rejectedFrameReason: '',
    });
    this.handleFileAdd(dataURL);
  };

  handleRetakePhoto = () => {
    this.setState({ rejectedFrameURL: null, rejectedFrameReason: '' }, () => {
      if (this.state.isMobile) this.handleOpenCameraMobile();
      else this.handleTakePhotoClick();
    });
  };

  stopCamera = () => {
    stopStream(this.videoRef.current);
    this.setState({ showVideo: false, cameraReady: false });
  };

  handleFileAdd = async (fileDataURL) => {
    try {
      const newFile = await dataURLToPhotoFile(fileDataURL);
      this.setState((prevState) => {
        prevState.files.forEach((oldFile) => {
          if (oldFile?.isBlob && oldFile.localURL) {
            URL.revokeObjectURL(oldFile.localURL);
          }
        });
        return {
          files: [newFile],
          newFiles: [newFile],
          capturedImageURL: newFile.localURL,
        };
      });
    } catch (err) {
      // The photo never made it into `files`, so Save stays blocked — say why
      // rather than leaving the employee with an unexplained disabled button.
      this.setState({
        capturedImageURL: null,
        cameraError:
          'The captured photo could not be prepared for upload. Please take it again.',
      });
    }
  };

  getFormattedFileObject = async (file) => {
    return {
      localURL: URL.createObjectURL(file),
      displayName: file.name,
      type: file.type,
      file,
    };
  };

  startCameraMobile = async (deviceId) => {
    stopStream(this.videoRef.current);
    this.setState({
      isCameraOpen: true,
      cameraStarting: true,
      cameraReady: false,
      cameraError: '',
    });
    try {
      const stream = await openCameraStream({
        // Manage Rule's general camera, unless the employee picked one.
        deviceId:
          deviceId ||
          this.state.selectedCameraId ||
          (await resolveCameraDeviceId(CAMERA_ROLES.GENERAL)),
        facingMode: this.state.cameraFacingMode,
      });
      if (!this.videoRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      await attachStream(this.videoRef.current, stream);
      const videoInputs = await listVideoInputs();
      this.setState({
        cameraStarting: false,
        cameraReady: true,
        cameraError: '',
        videoInputs,
      });
    } catch (err) {
      stopStream(this.videoRef.current);
      this.setState({
        isCameraOpen: false,
        cameraStarting: false,
        cameraReady: false,
        cameraError: describeCameraError(err),
      });
    }
  };

  switchCameraMobile = () => {
    const nextFacingMode =
      this.state.cameraFacingMode === 'environment' ? 'user' : 'environment';
    // Clear any pinned deviceId, otherwise it wins over the new facingMode.
    this.setState(
      { cameraFacingMode: nextFacingMode, selectedCameraId: '' },
      () => {
        if (this.state.isCameraOpen) {
          this.startCameraMobile();
        }
      }
    );
  };

  stopCameraMobile = () => {
    stopStream(this.videoRef.current);
    this.setState({ isCameraOpen: false, cameraReady: false });
  };

  capturePhotoMobile = () => {
    const result = captureFrame(this.videoRef.current, this.canvasRef.current, {
      quality: 0.8,
      square: true,
    });
    this.handleCaptureResult(result, this.stopCameraMobile);
  };

  handleOpenCameraMobile = () => {
    if (!this.state.isCameraOpen) {
      this.setState({ rejectedFrameURL: null, rejectedFrameReason: '' });
      this.startCameraMobile();
    }
  };

  // ── Validation ────────────────────────────────────────────────────────────
  // A photo is mandatory on registration. We gate on `files` rather than
  // `capturedImageURL` because `files` is what actually gets uploaded —
  // handleFileAdd fills it asynchronously (data URL → blob → File), so
  // capturedImageURL is briefly set while there is still nothing to send.
  hasPhoto = () => this.state.files.length > 0;

  isSaveDisabled = () => {
    const { firstName, lastName, phone, saving } = this.state;
    if (saving) return true;
    if (!firstName || !lastName) return true;
    if (!phone || phone.length !== 10) return true;
    // Photo is required — no customer may be registered without one.
    if (!this.hasPhoto()) return true;
    return false;
  };

  // Tells the employee WHICH gate is still blocking the save, so a disabled
  // button is never a dead end.
  saveButtonLabel = () => {
    const { saving } = this.state;
    if (saving) return 'Saving...';
    if (!this.hasPhoto()) return 'Take Photo to Save';
    return 'Save Customer';
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  handleSave = () => {
    if (this.isSaveDisabled()) return;

    const {
      firstName,
      lastName,
      phone,
      dob,
      comments,
      files,
      newFiles,
      capturedImageURL,
    } = this.state;

    const customer = {
      locationId: this.props.user.selectedLocationId,
      customerId: '',
      firstName,
      lastName,
      dob,
      points: DEFAULT_POINTS,
      driverLicense: '',
      phone,
      address: '',
      comments,
      files,
      newFiles,
      isRule: false,
      maxMatchesPerDay: null,
      timeLimit: null,
      maxAmountPerDay: null,
      isBlocked: false,
      matchValues: [],
      userId: this.props.user.userId,
      employeeName: [this.props.user?.firstname, this.props.user?.lastname]
        .filter((p) => p && p !== 'null' && p !== 'undefined')
        .join(' '),
      capturedImageURL,
      walletType: 'apple',
    };

    this.setState({ saving: true });

    this.props.saveCustomer(
      customer,
      (data) => {
        this.setState({ saving: false });
        this.props.openFeedbackBar('success', 'Saved');
        if (this.props.onCustomerSaved) {
          this.props.onCustomerSaved(data?.customer || null);
        } else {
          this.props.handleEditingClear();
        }
      },
      (message) => {
        this.setState({ saving: false });
        this.props.openFeedbackBar('error', message);
      }
    );
  };

  // Camera failure + rejected-frame messaging, shared by both layouts. This is
  // the surface that used to be missing entirely: getUserMedia errors were
  // swallowed and a blank frame looked exactly like a good one.
  renderCameraStatus = () => {
    const {
      cameraError,
      rejectedFrameURL,
      rejectedFrameReason,
      videoInputs,
      selectedCameraId,
      showVideo,
      isCameraOpen,
    } = this.state;

    const cameraOpen = showVideo || isCameraOpen;

    return (
      <React.Fragment>
        {!!cameraError && (
          <div className="customer-camera-error" role="alert">
            {cameraError}
          </div>
        )}

        {!!rejectedFrameURL && (
          <div className="customer-camera-warning" role="alert">
            <div className="customer-camera-warning-text">
              {rejectedFrameReason}
            </div>
            <img
              src={rejectedFrameURL}
              alt="Rejected capture"
              className="customer-camera-warning-thumb"
            />
            <div className="customer-camera-warning-actions">
              <Button
                variant="contained"
                className="customer-pill-btn"
                onClick={this.handleRetakePhoto}
                startIcon={<AddAPhoto />}
              >
                Retake Photo
              </Button>
              <Button
                size="small"
                className="customer-camera-override-btn"
                onClick={this.handleUseRejectedFrame}
              >
                Use anyway
              </Button>
            </div>
          </div>
        )}

        {cameraOpen && videoInputs.length > 1 && (
          <div className="customer-camera-picker">
            <label htmlFor="customer-camera-select">Camera</label>
            <select
              id="customer-camera-select"
              value={selectedCameraId}
              onChange={this.handleSelectCamera}
            >
              <option value="">Default camera</option>
              {videoInputs.map((d, i) => (
                <option key={d.deviceId || i} value={d.deviceId}>
                  {d.label || `Camera ${i + 1}`}
                </option>
              ))}
            </select>
          </div>
        )}
      </React.Fragment>
    );
  };

  renderPhotoDesktop = () => {
    return (
      <div className="customer-photo-section">
        {this.state.showVideo ? (
          <video
            ref={this.videoRef}
            className="customer-photo-video"
            autoPlay
            playsInline
            muted
          />
        ) : (
          <div className="customer-photo-circle">
            {this.state.capturedImageURL ? (
              <img
                src={this.state.capturedImageURL}
                alt="Captured"
                className="customer-photo-img"
              />
            ) : (
              <span className="customer-photo-placeholder">No Photo</span>
            )}
          </div>
        )}

        <canvas ref={this.canvasRef} style={{ display: 'none' }} />

        <div className="customer-photo-actions">
          {!this.state.showVideo && (
            <Button
              variant="contained"
              className="customer-pill-btn"
              onClick={this.handleTakePhotoClick}
              startIcon={<AddAPhoto />}
            >
              Open Camera
            </Button>
          )}
          {this.state.showVideo && (
            <Button
              variant="contained"
              className="customer-pill-btn"
              startIcon={<Camera />}
              onClick={this.captureImage}
              disabled={!this.state.cameraReady}
            >
              {this.state.cameraStarting
                ? 'Starting Camera...'
                : 'Capture Image'}
            </Button>
          )}
        </div>

        {this.renderCameraStatus()}

        {!this.state.showVideo &&
          !this.hasPhoto() &&
          !this.state.rejectedFrameURL && (
            <div className="customer-photo-required">Photo required</div>
          )}
      </div>
    );
  };

  renderPhotoMobile = () => {
    return (
      <div className="customer-photo-section">
        {!this.state.isCameraOpen && (
          <div className="customer-photo-circle">
            {this.state.capturedImageURL ? (
              <img
                src={this.state.capturedImageURL}
                alt="Captured"
                className="customer-photo-img"
              />
            ) : (
              <span className="customer-photo-placeholder">No Photo</span>
            )}
          </div>
        )}

        {!this.state.isCameraOpen && (
          <div className="customer-photo-actions">
            <Button
              variant="contained"
              className="customer-pill-btn"
              onClick={this.handleOpenCameraMobile}
              startIcon={<AddAPhoto />}
            >
              Open Camera
            </Button>
          </div>
        )}

        {!this.state.isCameraOpen &&
          !this.hasPhoto() &&
          !this.state.rejectedFrameURL && (
            <div className="customer-photo-required">Photo required</div>
          )}

        {this.renderCameraStatus()}

        {this.state.isCameraOpen && (
          <div className="video-container">
            <video
              ref={this.videoRef}
              className="responsive-video"
              autoPlay
              playsInline
              muted
            />
            <button
              style={styles.captureButton}
              onClick={this.capturePhotoMobile}
              disabled={!this.state.cameraReady}
            >
              <div style={styles.captureIcon}></div>
            </button>
            <button
              type="button"
              style={styles.flipButton}
              onClick={this.switchCameraMobile}
              aria-label="Switch Camera"
            >
              <FlipCameraIosIcon />
            </button>
          </div>
        )}

        <canvas ref={this.canvasRef} style={{ display: 'none' }} />
      </div>
    );
  };

  render() {
    return (
      <React.Fragment>
        <TopToolbar
          pageName={ADD_CUSTOMER_TOOLTIP}
          menuItems={[
            {
              title: BACK,
              icon: <KeyboardBackspaceIcon />,
              onClick: () => this.props.handleEditingClear(),
            },
          ]}
        />

        <div className="customer-add-wrapper">
          {this.state.isMobile
            ? this.renderPhotoMobile()
            : this.renderPhotoDesktop()}

          {!this.state.isCameraOpen && (
            <div className="customer-add-fields">
              {this.state.blacklistMatches.length > 0 &&
                !this.state.blacklistDismissed && (
                  <BlacklistWarningPanel
                    matches={this.state.blacklistMatches}
                    firstName={this.state.firstName}
                    onDismiss={() =>
                      this.setState({ blacklistDismissed: true })
                    }
                  />
                )}

              {this.state.duplicateNameMatches.length > 0 &&
                !this.state.duplicateNameDismissed && (
                  <DuplicateCustomerPanel
                    matches={this.state.duplicateNameMatches}
                    searchLabel={`${this.state.firstName.trim()} ${this.state.lastName.trim()}`}
                    matchType="name"
                    onDismiss={() =>
                      this.setState({ duplicateNameDismissed: true })
                    }
                  />
                )}

              {this.state.duplicatePhoneMatches.length > 0 &&
                !this.state.duplicatePhoneDismissed && (
                  <DuplicateCustomerPanel
                    matches={this.state.duplicatePhoneMatches}
                    searchLabel={this.state.phone}
                    matchType="phone"
                    onDismiss={() =>
                      this.setState({ duplicatePhoneDismissed: true })
                    }
                  />
                )}

              <TextField
                className="customer-add-input"
                variant="outlined"
                label="First Name"
                InputLabelProps={{ shrink: true }}
                name="firstName"
                value={this.state.firstName}
                onChange={this.handleNameChange}
                required
                fullWidth
              />

              <TextField
                className="customer-add-input"
                variant="outlined"
                label="Last Name"
                InputLabelProps={{ shrink: true }}
                name="lastName"
                value={this.state.lastName}
                onChange={this.handleNameChange}
                required
                fullWidth
              />

              <TextField
                className="customer-add-input"
                variant="outlined"
                label="Phone"
                InputLabelProps={{ shrink: true }}
                name="phone"
                value={this.state.phone}
                onChange={this.handlePhoneChange}
                required
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  autoOk
                  name="dob"
                  variant="inline"
                  inputVariant="outlined"
                  label="Date Of Birth"
                  className="customer-add-input"
                  format="MM/dd/yyyy"
                  placeholder="MM/DD/YYYY"
                  value={this.state.dob}
                  InputAdornmentProps={{ position: 'start' }}
                  onChange={(dateInput) => {
                    const date = new Date(dateInput?.toString());
                    if (date.toString() === 'Invalid Date') return;
                    this.handleDateChange(date, 'dob');
                  }}
                  fullWidth
                />
              </MuiPickersUtilsProvider>

              <TextField
                className="customer-add-input customer-field-wide"
                variant="outlined"
                label="Comments"
                InputLabelProps={{ shrink: true }}
                name="comments"
                value={this.state.comments}
                onChange={this.handleChange}
                multiline
                minRows={4}
                maxRows={6}
                fullWidth
              />

              <div className="customer-add-info-banner">
                <InfoOutlinedIcon className="customer-add-info-icon" />
                <span>{this.postSaveFeatureSentence()}</span>
              </div>

              <WriteGuard style={{ display: 'block' }}>
                <Button
                  className="customer-primary-btn"
                  onClick={this.handleSave}
                  disabled={this.isSaveDisabled()}
                  fullWidth
                >
                  {this.saveButtonLabel()}
                </Button>
              </WriteGuard>
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }
}

const styles = {
  captureButton: {
    position: 'fixed',
    bottom: '120px',
    left: '50%',
    transform: 'translateX(-50%)',
    border: 'none',
    borderRadius: '50%',
    width: '70px',
    height: '70px',
    backgroundColor: '#fff',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0',
  },
  captureIcon: {
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    backgroundColor: '#333',
  },
  flipButton: {
    position: 'fixed',
    bottom: '135px',
    right: '24px',
    border: 'none',
    borderRadius: '50%',
    width: '48px',
    height: '48px',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.3)',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0',
  },
};

export default CustomerForm;
